import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getErrorTrackingObject } from '@rci/self-serve-fido/core/analytics';
import { AuthenticationService } from '@rci/self-serve-fido/shared/authentication/authentication.service';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';
import { AccountHelper } from '@rci/self-serve-fido/state/account/account.helper';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { ApiEndpointService } from '@rci/self-serve-shared';
import { StateService } from '@rci/state-service';
import { CustomHttpErrorResponse, UserAccount } from '@rogers/self-serve-common';
import { AccountDetail, AccountSubscription, ServiceTypeEnum, SubTypeEnum } from '@rogers/self-serve-common/account';
import { getLatestValue } from '@rogers/self-serve-utils';
import { map } from 'rxjs/operators';
import { modalWindowConfig } from '../modal-window.config';
import { ViewContainerRefService } from '../view-container-ref/view-container-ref.service';
import { PHONE_NUMBER } from '@rci/self-serve-fido/app.constant';
import { LOCAL_STORAGE } from '@rci/storage/local-storage.service';

@Injectable()
export class CommonAppConfigFactory {
  constructor(
    private readonly apiEndpointService: ApiEndpointService,
    private readonly authenticationService: AuthenticationService,
    private readonly stateService: StateService,
    private readonly viewContainerRefService: ViewContainerRefService,
    private readonly translateService: TranslateService,
    private readonly userInformationStateFacade: UserInformationStateFacade,
    private readonly accountDetailService: AccountDetailService,
    @Inject(LOCAL_STORAGE) private readonly localStorage: Storage,
  ) {}

  get language() {
    return this.translateService.currentLang;
  }

  get currentPhoneNumber() {
    return this.localStorage.getItem(PHONE_NUMBER);
  }

  get selectedAccountNumber() {
    return this.userInformationStateFacade.selectedAccountNumber;
  }

  get selectedAccountNumber$() {
    return this.userInformationStateFacade.selectedAccountNumber$;
  }

  get wirelessApiUrl() {
    return this.apiEndpointService.wirelessBaseUrl;
  }

  get baseApiUrl() {
    return this.apiEndpointService.accountsMsBaseUrl;
  }

  get accountsApiUrl() {
    return this.apiEndpointService.accountsBaseUrl;
  }

  get contentBaseUrl() {
    return this.apiEndpointService.contentBaseUrl;
  }

  get contentApiUrlUnauthenticated() {
    return this.apiEndpointService.contentApiUrlUnauthenticated;
  }

  get financedAccessoriesBaseUrl() {
    return this.apiEndpointService.financedAccessoriesBaseUrl;
  }

  get modalWindowConfig() {
    return modalWindowConfig;
  }

  get viewContainerRef() {
    return this.viewContainerRefService.viewContainerRef;
  }

  get province$() {
    return this.stateService.province$;
  }

  get accountDetail$() {
    return this.accountDetailService.getSelectedAccountDetail$().pipe(
      map(selectedAccount => {
        const subscriptionDetails = selectedAccount.subscriptions.find(
          sub => sub.subNumber === this.currentPhoneNumber,
        );
        return {
          accountNumber: selectedAccount.accountNumber,
          accountAlias: selectedAccount.alias,
          subscription: {
            firstName: subscriptionDetails?.firstName,
            lastName: subscriptionDetails?.lastName,
            alias: subscriptionDetails?.alias,
            isLostOrStolen: subscriptionDetails?.isLostOrStolen,
          },
        };
      }),
    );
  }

  getHeaders() {
    return this.authenticationService.getDomainServiceHeaders(getLatestValue(this.stateService.language$));
  }

  getErrorTrackingObject(error: CustomHttpErrorResponse) {
    return getErrorTrackingObject(error);
  }

  fetchAccountDetail$(accountNumber, accountAlias, accountTypeNumber) {
    return this.accountDetailService.fetchAccountDetail$(accountNumber, accountAlias, accountTypeNumber);
  }

  setSelectedAccountNumber(accountNumber: string) {
    this.userInformationStateFacade.updateSelectedAccountNumber(accountNumber);
  }

  setSelectedSubscriptionNumber(subscriptionNumber: string) {
    this.userInformationStateFacade.updateSelectedSubscriberNo(subscriptionNumber);
  }

  transformAccountList(accounts: UserAccount[]): UserAccount[] {
    return AccountHelper.transformAccountList(accounts);
  }

  getBrandAccountDetail(subscriptions: AccountSubscription[], _accountDetail: AccountDetail, account: UserAccount) {
    if (!subscriptions || subscriptions.length === 0) {
      return {
        transformedSubscriptions: [],
        hasActiveWirelessSubscription: false,
        hasWirelessSubscription: false,
        hasResidentialSubscription: false,
        hasActiveResidentialSubscription: false,
      };
    }

    const transformedSubscriptions = this.getTransformedSubscriptions(subscriptions, account);
    const hasActiveWirelessSubscription = this.hasActiveWirelessSubscription(subscriptions);
    const hasWirelessSubscription = this.hasWirelessSubscription(subscriptions);
    const hasResidentialSubscription = this.hasResidentialSubscription(subscriptions);
    const hasActiveResidentialSubscription = this.hasActiveResidentialSubscription(subscriptions);
    return {
      transformedSubscriptions,
      hasActiveWirelessSubscription,
      hasWirelessSubscription,
      hasResidentialSubscription,
      hasActiveResidentialSubscription,
    };
  }

  private hasWirelessSubscription(subs: AccountSubscription[]) {
    return subs.some(sub => sub.type === ('CTN' as SubTypeEnum));
  }

  private hasActiveWirelessSubscription(subs) {
    return subs.some(sub => sub.type === ('CTN' as SubTypeEnum) && sub.isActive);
  }

  private hasResidentialSubscription(subs) {
    return subs.some(sub => sub.serviceType === (1 as ServiceTypeEnum));
  }

  private hasActiveResidentialSubscription(subs) {
    return subs.some(sub => sub.serviceType === (1 as ServiceTypeEnum) && sub.isActive);
  }

  private getTransformedSubscriptions(subscriptions: AccountSubscription[], account: UserAccount) {
    const activeSubscriptions = subscriptions.filter(subscription => subscription.isActive);
    const suspendedSubscriptions = subscriptions.filter(subscription => subscription.isSuspended);
    return [...activeSubscriptions, ...suspendedSubscriptions].map(subscription => ({
      ...subscription,
      isLostOrStolen: subscription.reason === 'LOS',
      overageProtectionProfileLink:
        `${this.apiEndpointService.wirelessBaseUrl}` +
        `${account.alias}/subscription/${subscription.alias}/data-overage-protection`,
    }));
  }
}
